import React from "react";
import { MousePointerClick, Trash2, Save, RotateCcw, X } from "lucide-react";
import { SIGNER_META, FIELD_LABEL } from "@/components/contracts/contractUtils";

const TYPES = Object.keys(FIELD_LABEL);
const ROLES = Object.keys(SIGNER_META);

function Inspector({ field, onChange, onDelete, locked }) {
  if (!field) return <div className="text-xs text-[var(--ff-text-dim)]">Click a field on the document to edit it. Drag to move, drag the corner to resize.</div>;
  if (locked) return <div className="text-xs text-yellow-300">This signer already signed — their fields are locked.</div>;
  return (
    <div className="space-y-2" data-testid="field-inspector">
      <div className="grid grid-cols-2 gap-2">
        <select value={field.type} onChange={(e) => onChange({ type: e.target.value })} className="px-2 py-1.5 text-xs" data-testid="inspector-type">
          {TYPES.map((t) => <option key={t} value={t}>{FIELD_LABEL[t]}</option>)}
        </select>
        <select value={field.signer} onChange={(e) => onChange({ signer: e.target.value })} className="px-2 py-1.5 text-xs" data-testid="inspector-signer">
          {ROLES.map((r) => <option key={r} value={r}>{SIGNER_META[r].label}</option>)}
        </select>
      </div>
      <input value={field.label || ""} onChange={(e) => onChange({ label: e.target.value })} placeholder="Label" className="w-full px-2 py-1.5 text-xs" data-testid="inspector-label" />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input type="checkbox" checked={field.required} onChange={(e) => onChange({ required: e.target.checked })} data-testid="inspector-required" /> Required
        </label>
        <button onClick={onDelete} className="text-[var(--ff-red)] text-xs inline-flex items-center gap-1" data-testid="inspector-delete"><Trash2 size={12} /> Delete</button>
      </div>
    </div>
  );
}

export default function FieldToolbar({ placing, setPlacing, draft, setDraft, selected, lockedRoles, onChange, onDelete, onSave, onReset, onCancel, dirty, count }) {
  return (
    <div className="surface-card p-5 space-y-4" data-testid="field-toolbar">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">▮ FIELDS · {count}</div>
        <button onClick={onCancel} className="text-[var(--ff-text-dim)] hover:text-white" data-testid="fields-cancel-btn"><X size={14} /></button>
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} className="px-2 py-1.5 text-xs" data-testid="new-field-type">
            {TYPES.map((t) => <option key={t} value={t}>{FIELD_LABEL[t]}</option>)}
          </select>
          <select value={draft.signer} onChange={(e) => setDraft({ ...draft, signer: e.target.value })} className="px-2 py-1.5 text-xs" data-testid="new-field-signer">
            {ROLES.map((r) => <option key={r} value={r}>{SIGNER_META[r].label}</option>)}
          </select>
        </div>
        <button onClick={() => setPlacing(!placing)} className={`${placing ? "btn-primary" : "btn-ghost inline-flex items-center gap-1.5"} text-xs w-full justify-center`} data-testid="place-field-btn">
          <MousePointerClick size={12} /> {placing ? "Click on the document to place…" : "Place Field"}
        </button>
      </div>
      <div className="border-t border-[var(--ff-border)] pt-4">
        <Inspector field={selected} onChange={onChange} onDelete={onDelete} locked={selected && lockedRoles.has(selected.signer)} />
      </div>
      <div className="flex gap-2">
        <button onClick={onSave} disabled={!dirty} className="btn-primary text-xs flex-1 justify-center disabled:opacity-40" data-testid="fields-save-btn"><Save size={12} /> Save Fields</button>
        <button onClick={onReset} className="btn-ghost inline-flex items-center gap-1.5 text-xs" title="Reset to auto-detected" data-testid="fields-reset-btn"><RotateCcw size={12} /></button>
      </div>
    </div>
  );
}
